import { SUPABASE_ANON_KEY, SUPABASE_URL, supabase } from '../supabaseClient';
import { PRODUCTION_SITE_ORIGIN } from '../constants/publicSite';
import type { TodoNoteCategoryDraft, TodoNoteItemDraft } from '../types/todoNote';
import { defaultRoutineConfig } from '../utils/todoRoutine';
import {
  parseTodoPostitLayout,
  type TodoPostitPosition,
} from '../utils/todoNotePostitLayout';

const SESSION_STORAGE_PREFIX = 'cp.todoShare.session.v1:';

export type TodoShareOwnerProfile = {
  display_name: string;
  team_display_name: string;
  role: string;
  has_active_team: boolean;
};

export type TodoShareBoardMeta = {
  id: string;
  public_token: string;
  title: string;
  revision: number;
  is_active: boolean;
  workspace_id: string | null;
  /** 소유자 UI용 적용 입장코드 (게스트 응답에는 포함되지 않음) */
  entry_code?: string | null;
  /** personal_default: 개인 노트에서 이전된 기본 보드 */
  source_kind?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type TodoShareSnapshot = {
  board_id: string;
  title: string;
  revision: number;
  public_token?: string;
  is_active?: boolean;
  owner_profile?: TodoShareOwnerProfile | null;
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
  postit_layout: Record<string, TodoPostitPosition>;
};

export type TodoShareSavePayload = {
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
  deleted_category_ids: string[];
  deleted_item_ids: string[];
};

export class TodoShareRevisionConflictError extends Error {
  constructor(message = 'REVISION_CONFLICT') {
    super(message);
    this.name = 'TodoShareRevisionConflictError';
  }
}

function formatShareRpcError(message: string): string {
  const msg = String(message ?? '');
  if (/BOARD_PRIVATE|BOARD_INACTIVE/i.test(msg)) return '이 보드는 비공개입니다.';
  if (/INVALID_CREDENTIALS/i.test(msg)) return '입장코드가 올바르지 않거나 보드가 비공개입니다.';
  if (/INVALID_SESSION/i.test(msg)) return '공유 세션이 만료되었습니다. 입장코드를 다시 입력해 주세요.';
  if (/REVISION_CONFLICT/i.test(msg)) return 'REVISION_CONFLICT';
  if (/INVALID_CODE/i.test(msg)) return '입장코드는 4~32자로 입력해 주세요.';
  if (/NOT_AUTHENTICATED/i.test(msg)) return '로그인이 필요합니다.';
  if (/NOT_FOUND/i.test(msg)) return '공유 보드를 찾을 수 없습니다.';
  if (/DEFAULT_BOARD_RENAME_FORBIDDEN/i.test(msg)) return '기본 노트 이름은 변경할 수 없습니다.';
  if (/DEFAULT_BOARD_DELETE_FORBIDDEN/i.test(msg)) return '기본 노트는 삭제할 수 없습니다.';
  if (/no api key found/i.test(msg)) {
    return 'Supabase API 키가 요청에 포함되지 않았습니다. 개발 서버를 재시작한 뒤 다시 시도해 주세요.';
  }
  return msg || '공유 ToDo 요청에 실패했습니다.';
}

async function postShareRpc<T>(
  functionName: string,
  body: Record<string, unknown>,
  bearerToken?: string
): Promise<T> {
  const baseUrl = SUPABASE_URL.replace(/\/$/, '');
  const anonKey = SUPABASE_ANON_KEY.trim();
  if (!baseUrl || !anonKey) {
    throw new Error('Supabase 환경변수가 설정되지 않았습니다.');
  }

  let token = bearerToken;
  if (!token) {
    const { data } = await supabase.auth.getSession();
    token = data.session?.access_token || anonKey;
  }

  const res = await fetch(`${baseUrl}/rest/v1/rpc/${functionName}`, {
    method: 'POST',
    headers: {
      apikey: anonKey,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  const rawText = await res.text();
  let parsed: unknown = null;
  if (rawText) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const errBody = parsed as { message?: string; code?: string } | null;
    const message = errBody?.message || rawText || `HTTP ${res.status}`;
    const normalized = formatShareRpcError(message);
    if (normalized === 'REVISION_CONFLICT') {
      throw new TodoShareRevisionConflictError();
    }
    throw new Error(normalized);
  }

  return parsed as T;
}

function mapShareCategory(raw: Record<string, unknown>, boardId: string): TodoNoteCategoryDraft {
  return {
    id: String(raw.id),
    user_id: '',
    workspace_id: boardId,
    name: String(raw.name ?? ''),
    color: String(raw.color ?? '#ffffff'),
    sort_order: Number(raw.sort_order ?? 0),
    note_memo: typeof raw.note_memo === 'string' ? raw.note_memo : '',
    created_at: typeof raw.created_at === 'string' ? raw.created_at : undefined,
    updated_at: typeof raw.updated_at === 'string' ? raw.updated_at : undefined,
  };
}

function parseRoutine(raw: unknown): TodoNoteItemDraft['routine'] {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const frequency =
    o.frequency === 'weekly' || o.frequency === 'monthly' || o.frequency === 'daily'
      ? o.frequency
      : 'daily';
  const displayRaw = o.display && typeof o.display === 'object' ? (o.display as Record<string, unknown>) : {};
  const dayLogsRaw =
    o.dayLogs && typeof o.dayLogs === 'object'
      ? (o.dayLogs as Record<string, unknown>)
      : o.day_logs && typeof o.day_logs === 'object'
        ? (o.day_logs as Record<string, unknown>)
        : {};
  const dayLogs: NonNullable<TodoNoteItemDraft['routine']>['dayLogs'] = {};
  for (const [ymd, log] of Object.entries(dayLogsRaw)) {
    if (!log || typeof log !== 'object') continue;
    const L = log as Record<string, unknown>;
    const override =
      L.completedOverride === true || L.completed_override === true
        ? true
        : undefined;
    if (override === true) {
      dayLogs[ymd] = { completedOverride: true };
    }
  }
  return {
    frequency,
    weekdays: Array.isArray(o.weekdays) ? o.weekdays.map((n) => Number(n)).filter((n) => n >= 0 && n <= 6) : undefined,
    monthDay: typeof o.monthDay === 'number' ? o.monthDay : typeof o.month_day === 'number' ? o.month_day : undefined,
    startDate:
      typeof o.startDate === 'string' ? o.startDate.slice(0, 10) : typeof o.start_date === 'string' ? o.start_date.slice(0, 10) : null,
    endDate:
      typeof o.endDate === 'string' ? o.endDate.slice(0, 10) : typeof o.end_date === 'string' ? o.end_date.slice(0, 10) : null,
    display: {
      unit: displayRaw.unit === 'month' ? 'month' : 'week',
      before: Math.max(0, Number(displayRaw.before ?? 2) || 0),
      after: Math.max(0, Number(displayRaw.after ?? 4) || 0),
    },
    dayLogs,
  };
}

function mapShareItem(raw: Record<string, unknown>, boardId: string): TodoNoteItemDraft {
  const isRoutine = Boolean(raw.is_routine);
  const routine = parseRoutine(raw.routine);
  return {
    id: String(raw.id),
    user_id: '',
    workspace_id: boardId,
    category_id: raw.category_id == null || raw.category_id === '' ? null : String(raw.category_id),
    due_kind: raw.due_kind === 'month' ? 'month' : 'day',
    due_date: String(raw.due_date ?? '').slice(0, 10),
    title: String(raw.title ?? ''),
    body: String(raw.body ?? ''),
    misc: String(raw.misc ?? ''),
    completed: Boolean(raw.completed),
    bucket_order: Number(raw.bucket_order ?? 0),
    is_routine: isRoutine,
    // is_routine만 있고 routine jsonb가 비어 있으면 기본 규칙으로라도 루틴으로 복원
    routine: isRoutine ? routine ?? defaultRoutineConfig() : routine,
    is_favorite: Boolean(raw.is_favorite),
    created_at: typeof raw.created_at === 'string' ? raw.created_at : undefined,
    updated_at: typeof raw.updated_at === 'string' ? raw.updated_at : undefined,
  };
}

function mapOwnerProfile(raw: unknown): TodoShareOwnerProfile | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const display = String(o.display_name ?? '').trim();
  if (!display) return null;
  return {
    display_name: display,
    team_display_name: String(o.team_display_name ?? '').trim(),
    role: String(o.role ?? 'teacher').trim() || 'teacher',
    has_active_team: Boolean(o.has_active_team),
  };
}

function parseSnapshot(raw: Record<string, unknown>): TodoShareSnapshot {
  const boardId = String(raw.board_id ?? '');
  const cats = Array.isArray(raw.categories) ? raw.categories : [];
  const items = Array.isArray(raw.items) ? raw.items : [];
  return {
    board_id: boardId,
    title: String(raw.title ?? '공유 ToDo'),
    revision: Number(raw.revision ?? 1),
    public_token: typeof raw.public_token === 'string' ? raw.public_token : undefined,
    is_active: typeof raw.is_active === 'boolean' ? raw.is_active : undefined,
    owner_profile: mapOwnerProfile(raw.owner_profile),
    categories: cats.map((c) => mapShareCategory(c as Record<string, unknown>, boardId)),
    items: items.map((it) => mapShareItem(it as Record<string, unknown>, boardId)),
    postit_layout: parseTodoPostitLayout(raw.postit_layout),
  };
}

function categoryPayload(c: TodoNoteCategoryDraft) {
  return {
    id: c.id,
    name: c.name.trim(),
    color: c.color,
    sort_order: c.sort_order,
    note_memo: c.note_memo ?? '',
  };
}

function itemPayload(it: TodoNoteItemDraft) {
  const isRoutine = Boolean(it.is_routine && it.routine);
  return {
    id: it.id,
    category_id: it.category_id,
    due_kind: it.due_kind,
    due_date: it.due_date,
    title: it.title,
    body: it.body,
    misc: it.misc,
    completed: it.completed,
    bucket_order: it.bucket_order,
    is_routine: isRoutine,
    routine: isRoutine ? defaultRoutineConfig(it.routine ?? undefined) : null,
    is_favorite: Boolean(it.is_favorite),
  };
}

/** 서버에 반영된 상태의 항목별 저장 페이로드 지문 (변경분만 보내기 위한 비교 기준) */
export type TodoShareSavedFingerprint = {
  categories: Map<string, string>;
  items: Map<string, string>;
};

export function buildTodoShareSavedFingerprint(
  categories: TodoNoteCategoryDraft[],
  items: TodoNoteItemDraft[]
): TodoShareSavedFingerprint {
  return {
    categories: new Map(categories.map((c) => [c.id, JSON.stringify(categoryPayload(c))])),
    items: new Map(items.map((it) => [it.id, JSON.stringify(itemPayload(it))])),
  };
}

/** 지문과 저장 페이로드가 달라진(또는 새) 카테고리·항목만 고른다. 지문이 없으면 전체. */
export function pickTodoShareChangedForSave(
  fingerprint: TodoShareSavedFingerprint | null,
  categories: TodoNoteCategoryDraft[],
  items: TodoNoteItemDraft[]
): { categories: TodoNoteCategoryDraft[]; items: TodoNoteItemDraft[] } {
  if (!fingerprint) return { categories, items };
  return {
    categories: categories.filter(
      (c) => fingerprint.categories.get(c.id) !== JSON.stringify(categoryPayload(c))
    ),
    items: items.filter((it) => fingerprint.items.get(it.id) !== JSON.stringify(itemPayload(it))),
  };
}

export function buildTodoShareSavePayload(params: {
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
  deletedCategoryIds: string[];
  deletedItemIds: string[];
}): TodoShareSavePayload {
  return {
    categories: params.categories,
    items: params.items,
    deleted_category_ids: params.deletedCategoryIds,
    deleted_item_ids: params.deletedItemIds,
  };
}

function sessionStorageKey(publicToken: string): string {
  return `${SESSION_STORAGE_PREFIX}${publicToken}`;
}

export type StoredTodoShareSession = {
  access_token: string;
  expires_at: string;
  board_id: string;
  title: string;
  owner_profile?: TodoShareOwnerProfile | null;
};

export function readStoredTodoShareSession(publicToken: string): StoredTodoShareSession | null {
  try {
    const raw = sessionStorage.getItem(sessionStorageKey(publicToken));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredTodoShareSession;
    if (!parsed?.access_token) return null;
    if (parsed.expires_at && Date.parse(parsed.expires_at) <= Date.now()) {
      sessionStorage.removeItem(sessionStorageKey(publicToken));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeStoredTodoShareSession(
  publicToken: string,
  session: StoredTodoShareSession
): void {
  try {
    sessionStorage.setItem(sessionStorageKey(publicToken), JSON.stringify(session));
  } catch {
    /* ignore */
  }
}

export function clearStoredTodoShareSession(publicToken: string): void {
  try {
    sessionStorage.removeItem(sessionStorageKey(publicToken));
  } catch {
    /* ignore */
  }
}

/** 공유 링크는 로컬에서도 배포 사이트 주소를 사용 */
const TODO_SHARE_PUBLIC_SITE_ORIGIN = (
  process.env.REACT_APP_PUBLIC_SITE_URL || PRODUCTION_SITE_ORIGIN
).replace(/\/$/, '');

export function getTodoSharePublicUrl(publicToken: string): string {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${TODO_SHARE_PUBLIC_SITE_ORIGIN}${base}/t/${encodeURIComponent(publicToken)}`;
}

/** 게스트: 링크 토큰만으로 제목·소유자 프로필(입장 전 네비용) */
export async function loadTodoSharePublicChrome(publicToken: string): Promise<{
  title: string;
  owner_profile: TodoShareOwnerProfile | null;
}> {
  const data = await postShareRpc<{
    title?: string;
    owner_profile?: unknown;
  }>('todo_share_public_chrome', { p_token: publicToken }, SUPABASE_ANON_KEY);
  return {
    title: String(data.title ?? ''),
    owner_profile: mapOwnerProfile(data.owner_profile),
  };
}

/** 게스트: 링크 토큰 + 입장코드 → access_token */
export async function verifyTodoShareAccess(
  publicToken: string,
  code: string
): Promise<StoredTodoShareSession> {
  const data = await postShareRpc<{
    access_token: string;
    expires_at: string;
    board_id: string;
    title: string;
    revision: number;
    owner_profile?: unknown;
  }>(
    'todo_share_verify',
    { p_token: publicToken, p_code: code },
    SUPABASE_ANON_KEY
  );
  const session: StoredTodoShareSession = {
    access_token: data.access_token,
    expires_at: data.expires_at,
    board_id: data.board_id,
    title: data.title,
    owner_profile: mapOwnerProfile(data.owner_profile),
  };
  writeStoredTodoShareSession(publicToken, session);
  return session;
}

export async function loadTodoShareByAccessToken(accessToken: string): Promise<TodoShareSnapshot> {
  const data = await postShareRpc<Record<string, unknown>>(
    'todo_share_load',
    { p_access_token: accessToken },
    SUPABASE_ANON_KEY
  );
  return parseSnapshot(data);
}

export async function saveTodoShareByAccessToken(params: {
  accessToken: string;
  expectedRevision: number;
  payload: TodoShareSavePayload;
}): Promise<number> {
  const data = await postShareRpc<{ revision: number }>(
    'todo_share_save',
    {
      p_access_token: params.accessToken,
      p_expected_revision: params.expectedRevision,
      p_payload: {
        categories: params.payload.categories.map(categoryPayload),
        items: params.payload.items.map(itemPayload),
        deleted_category_ids: params.payload.deleted_category_ids,
        deleted_item_ids: params.payload.deleted_item_ids,
      },
    },
    SUPABASE_ANON_KEY
  );
  return Number(data.revision);
}

function mapBoardMeta(raw: Partial<TodoShareBoardMeta> & { id?: string }): TodoShareBoardMeta {
  return {
    id: String(raw.id ?? ''),
    public_token: String(raw.public_token ?? ''),
    title: String(raw.title ?? '새 노트'),
    revision: Number(raw.revision ?? 1),
    is_active: Boolean(raw.is_active ?? false),
    workspace_id: raw.workspace_id ?? null,
    entry_code: typeof raw.entry_code === 'string' ? raw.entry_code : raw.entry_code ?? null,
    source_kind: typeof raw.source_kind === 'string' ? raw.source_kind : raw.source_kind ?? null,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
  };
}

export async function createTodoShareBoard(params: {
  title: string;
  code?: string | null;
  workspaceId: string | null;
}): Promise<TodoShareBoardMeta> {
  const data = await postShareRpc<TodoShareBoardMeta>('todo_share_create_board', {
    p_title: params.title,
    p_code: params.code?.trim() ? params.code.trim() : null,
    p_workspace_id: params.workspaceId,
  });
  return mapBoardMeta({
    ...data,
    entry_code: data.entry_code ?? (params.code?.trim() || null),
  });
}

export async function migratePersonalTodoToDefaultBoard(
  workspaceId: string | null
): Promise<TodoShareBoardMeta> {
  const data = await postShareRpc<TodoShareBoardMeta & { migrated?: boolean }>(
    'todo_share_migrate_personal_default',
    { p_workspace_id: workspaceId }
  );
  return mapBoardMeta(data);
}

export async function listTodoShareBoards(
  workspaceId: string | null
): Promise<TodoShareBoardMeta[]> {
  const data = await postShareRpc<TodoShareBoardMeta[]>('todo_share_list_boards', {
    p_workspace_id: workspaceId,
  });
  return Array.isArray(data) ? data.map((row) => mapBoardMeta(row)) : [];
}

export async function setTodoShareBoardActive(
  boardId: string,
  isActive: boolean
): Promise<TodoShareBoardMeta> {
  return postShareRpc<TodoShareBoardMeta>('todo_share_set_board_active', {
    p_board_id: boardId,
    p_is_active: isActive,
  });
}

export async function rotateTodoShareCode(
  boardId: string,
  newCode: string
): Promise<{ entry_code: string }> {
  const data = await postShareRpc<{ entry_code?: string }>('todo_share_rotate_code', {
    p_board_id: boardId,
    p_new_code: newCode,
  });
  return { entry_code: typeof data?.entry_code === 'string' ? data.entry_code : newCode };
}

export async function renameTodoShareBoard(boardId: string, title: string): Promise<void> {
  await postShareRpc('todo_share_rename_board', {
    p_board_id: boardId,
    p_title: title,
  });
}

export async function deleteTodoShareBoard(boardId: string): Promise<void> {
  await postShareRpc('todo_share_delete_board', { p_board_id: boardId });
}

export async function loadTodoShareAsOwner(boardId: string): Promise<TodoShareSnapshot> {
  const data = await postShareRpc<Record<string, unknown>>('todo_share_owner_load', {
    p_board_id: boardId,
  });
  return parseSnapshot(data);
}

export async function saveTodoShareAsOwner(params: {
  boardId: string;
  expectedRevision: number;
  payload: TodoShareSavePayload;
}): Promise<number> {
  const data = await postShareRpc<{ revision: number }>('todo_share_owner_save', {
    p_board_id: params.boardId,
    p_expected_revision: params.expectedRevision,
    p_payload: {
      categories: params.payload.categories.map(categoryPayload),
      items: params.payload.items.map(itemPayload),
      deleted_category_ids: params.payload.deleted_category_ids,
      deleted_item_ids: params.payload.deleted_item_ids,
    },
  });
  return Number(data.revision);
}

export async function loadTodoSharePostitLayout(
  boardId: string
): Promise<Record<string, TodoPostitPosition>> {
  const data = await postShareRpc<{ postit_layout?: unknown }>('todo_share_owner_load_postit_layout', {
    p_board_id: boardId,
  });
  return parseTodoPostitLayout(data?.postit_layout);
}

export async function saveTodoSharePostitLayout(params: {
  boardId: string;
  layout: Record<string, TodoPostitPosition>;
}): Promise<void> {
  await postShareRpc('todo_share_owner_set_postit_layout', {
    p_board_id: params.boardId,
    p_layout: parseTodoPostitLayout(params.layout),
  });
}
